"use client";

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import Image from "next/image";
import { User } from "lucide-react";
import type { MentionItem, MentionSuggestionProps } from "editor";

export interface MentionSuggestionListHandle {
  onKeyDown: (event: KeyboardEvent) => boolean;
}

/**
 * Dropdown list shown during @mention autocomplete.
 * Exposed via forwardRef so the Tiptap suggestion lifecycle can delegate
 * keyboard events (up/down/enter/escape) to this component.
 */
const MentionSuggestionList = forwardRef<
  MentionSuggestionListHandle,
  MentionSuggestionProps
>(function MentionSuggestionList({ items, command }, ref) {
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    setSelectedIndex(0);
  }, [items]);

  const selectItem = (item: MentionItem): void => {
    command(item);
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: (event: KeyboardEvent): boolean => {
      if (event.key === "ArrowUp") {
        setSelectedIndex((i) => (i + items.length - 1) % items.length);
        return true;
      }
      if (event.key === "ArrowDown") {
        setSelectedIndex((i) => (i + 1) % items.length);
        return true;
      }
      if (event.key === "Enter") {
        const item = items[selectedIndex];
        if (item) selectItem(item);
        return true;
      }
      if (event.key === "Escape") {
        return true;
      }
      return false;
    },
  }));

  if (items.length === 0) {
    return (
      <div className="rounded-md border border-border bg-popover px-3 py-2 text-sm text-muted-foreground shadow-md">
        No members found
      </div>
    );
  }

  return (
    <div
      role="listbox"
      className="z-50 min-w-[200px] overflow-hidden rounded-md border border-border bg-popover shadow-md"
    >
      {items.map((item, index) => (
        <button
          key={item.id}
          role="option"
          aria-selected={index === selectedIndex}
          type="button"
          onMouseDown={(e) => {
            // Prevent the editor from losing focus before the click
            // handler runs (which would dismiss the popover).
            e.preventDefault();
          }}
          onClick={() => selectItem(item)}
          onMouseEnter={() => setSelectedIndex(index)}
          className={[
            "flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-popover-foreground transition-colors",
            index === selectedIndex ? "bg-accent" : "hover:bg-accent",
          ].join(" ")}
        >
          {item.image ? (
            <Image
              src={item.image}
              alt={item.label}
              width={24}
              height={24}
              className="h-6 w-6 shrink-0 rounded-md object-cover"
            />
          ) : (
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-muted">
              <User className="h-3 w-3 text-muted-foreground" />
            </div>
          )}
          <span className="truncate font-medium">{item.label}</span>
        </button>
      ))}
    </div>
  );
});

export default MentionSuggestionList;
